import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import PublicLayout from "./components/site/PublicLayout";
import Home from "./pages/Home";
import CategoryPage from "./pages/CategoryPage";
import ArticlePage from "./pages/ArticlePage";
import SearchPage from "./pages/SearchPage";
import ContactPage from "./pages/ContactPage";
import CancelPage from "./pages/CancelPage";
import InfoPage from "./pages/InfoPages";
import About from "./pages/About";
import GuidesDirectory from "./pages/guides/GuidesDirectory";
import NotFound from "./pages/NotFound";
import AdminRoutes from "./admin/AdminRoutes";
import { ScriptProvider } from "./lib/script";
import { ReaderProvider } from "./context/ReaderContext";
import { Registracija, Prijava, ZaboravljenaLozinka } from "./pages/reader/ReaderAuth";
import MojBalkan from "./pages/reader/MojBalkan";
import MojeVijesti from "./pages/reader/MojeVijesti";
import { Sacuvano, Obavjestenja, MojProfil } from "./pages/reader/ReaderAreas";
import Privatnost from "./pages/reader/Privatnost";
import { NewsletterConfirm, NewsletterUnsubscribe } from "./pages/Newsletter";
import { ConsentBanner, PageViewTracker } from "./components/site/Consent";
import { initAnalytics } from "./lib/analytics";
import { PartnerDirectory, PartnerProfile, BecomePartner } from "./pages/partners/PartnerPublic";
import { Advertising } from "./pages/partners/Commercial";
import { PitajBalkan, Razgovori } from "./pages/ai/PitajBalkan";
import { ToolsHub, ToolPage } from "./pages/tools/Tools";
import { GuidePage } from "./pages/guides/Guide";
import PremiumPage from "./pages/Premium";
import LegalPage from "./pages/LegalPage";
import FormsPage, { FormDetail } from "./pages/Forms";
import { PartnerProvider, PartnerRegister, PartnerLogin, PartnerForgot, PartnerDashboard } from "./pages/partners/PartnerPortal";

initAnalytics();

const INFO = ["o-nama"];
const LEGAL = ["impressum", "datenschutz", "agb", "nutzungsbedingungen", "premium-bedingungen", "werberichtlinien", "ki-transparenz", "cookie-richtlinie", "widerruf", "kuendigung", "urheberrecht", "redaktionelle-richtlinien", "korrekturen", "partnerbedingungen", "marketingbedingungen", "barrierefreiheit"];

const Toasts = () => {
  const admin = useLocation().pathname.startsWith("/admin");
  return <Toaster richColors position={admin ? "top-right" : "bottom-right"} offset={admin ? 76 : undefined} />;
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="bp-theme">
        <AuthProvider>
          <ScriptProvider>
          <ReaderProvider>
          <PartnerProvider>
          <BrowserRouter>
            <PageViewTracker />
            <ConsentBanner />
            <Routes>
              <Route path="/admin/*" element={<AdminRoutes />} />
              <Route element={<PublicLayout />}>
                <Route index element={<Home />} />
                <Route path="vijesti" element={<CategoryPage mode="all" />} />
                <Route path="najnovije" element={<CategoryPage mode="latest" />} />
                <Route path="pretraga" element={<SearchPage />} />
                <Route path="kontakt" element={<ContactPage />} />
                <Route path="vertraege-kuendigen" element={<CancelPage />} />
                <Route path="registracija" element={<Registracija />} />
                <Route path="prijava" element={<Prijava />} />
                <Route path="zaboravljena-lozinka" element={<ZaboravljenaLozinka />} />
                <Route path="moj-balkan-info" element={<MojBalkan />} />
                <Route path="pitaj-balkan-ai" element={<PitajBalkan />} />
                <Route path="ai/razgovori" element={<Razgovori />} />
                <Route path="ai/razgovor/:id" element={<PitajBalkan />} />
                <Route path="premium" element={<PremiumPage />} />
                <Route path="alati" element={<ToolsHub />} />
                <Route path="formulari" element={<FormsPage />} />
                <Route path="formulari/:slug" element={<FormDetail />} />
                <Route path="alati/:slug" element={<ToolPage />} />
                <Route path="vodici" element={<GuidesDirectory />} />
                <Route path="vodici/boravisne-dozvole" element={<GuidePage slug="boravisne-dozvole" />} />
                <Route path="vodici/priznavanje-diploma" element={<GuidePage slug="priznavanje-diploma" />} />
                <Route path="vodici/prvi-koraci" element={<GuidePage slug="prvi-koraci" />} />
                <Route path="vodic/:slug" element={<GuidePage />} />
                <Route path="moje-vijesti" element={<MojeVijesti />} />
                <Route path="sacuvano" element={<Sacuvano />} />
                <Route path="obavjestenja" element={<Obavjestenja />} />
                <Route path="moj-profil" element={<MojProfil />} />
                <Route path="postavke-privatnosti" element={<Privatnost />} />
                <Route path="newsletter/potvrda" element={<NewsletterConfirm />} />
                <Route path="newsletter/odjava" element={<NewsletterUnsubscribe />} />
                <Route path="partneri" element={<PartnerDirectory />} />
                <Route path="partneri/kategorija/:slug" element={<PartnerDirectory />} />
                <Route path="partneri/:slug" element={<PartnerProfile />} />
                <Route path="postani-partner" element={<BecomePartner />} />
                <Route path="oglasavanje" element={<Advertising />} />
                <Route path="partner/registracija" element={<PartnerRegister />} />
                <Route path="partner/prijava" element={<PartnerLogin />} />
                <Route path="partner/zaboravljena-lozinka" element={<PartnerForgot />} />
                <Route path="partner/dashboard" element={<PartnerDashboard />} />
                <Route path="partner/dashboard/:section" element={<PartnerDashboard />} />
                {INFO.map((p) => <Route key={p} path={p} element={<About />} />)}
                {LEGAL.map((p) => <Route key={p} path={p} element={<LegalPage slug={p} />} />)}
                {LEGAL.map((p) => <Route key={`${p}-v`} path={`${p}/verzija/:version`} element={<LegalPage slug={p} />} />)}
                <Route path="ispravke" element={<Navigate to="/korrekturen" replace />} />
                <Route path="urednicka-politika" element={<Navigate to="/redaktionelle-richtlinien" replace />} />
                <Route path=":categorySlug" element={<CategoryPage />} />
                <Route path=":categorySlug/:articleSlug" element={<ArticlePage />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
            <Toasts />
          </BrowserRouter>
          </PartnerProvider>
          </ReaderProvider>
          </ScriptProvider>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
